import { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout as AntLayout, Menu } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import {
  buildMainMenuItems,
  getOpenMenuKeys,
  getSelectedMenuKey,
  LOGOUT_MENU_KEY,
} from '@/app/routeRegistry';
import { useAuth } from '@/features/auth/AuthProvider';
import styles from './MainLayout.module.scss';

const { Sider, Content } = AntLayout;

export function MainLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const path = location.pathname;
  const menuItems = useMemo(() => buildMainMenuItems(currentUser), [currentUser]);
  const selectedKey = getSelectedMenuKey(path);
  const [openKeys, setOpenKeys] = useState<Array<string>>(() => getOpenMenuKeys(path));

  useEffect(() => {
    const groupKeys = getOpenMenuKeys(path);
    if (groupKeys.length === 0) {
      return;
    }

    setOpenKeys((currentKeys) => {
      const nextKeys = [...currentKeys];
      for (const groupKey of groupKeys) {
        if (!nextKeys.includes(groupKey)) {
          nextKeys.push(groupKey);
        }
      }

      return nextKeys;
    });
  }, [path]);

  return (
    <AntLayout className={styles.layout}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={200}
        collapsedWidth={64}
        className={styles.sider}
      >
        <div className={`${styles.logo} ${collapsed ? styles.logoCollapsed : ''}`}>
          {!collapsed && <span className={styles.logoText}>VP Quant</span>}
        </div>
        <Menu
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          openKeys={openKeys}
          items={menuItems}
          onOpenChange={(keys) => setOpenKeys(keys as Array<string>)}
          onClick={({ key }) => {
            if (key === LOGOUT_MENU_KEY) {
              void logout();
              return;
            }

            navigate(String(key));
          }}
          className={styles.menu}
        />
        <div className={styles.trigger} onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </div>
      </Sider>
      <AntLayout>
        <Content className={styles.content}>
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
}
